// src/VietnamApp.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import { RequirementsOverview } from './RequirementsOverview';
import { DocumentUpload } from './DocumentUpload';
import { PassportScanner } from './PassportScanner';
import { SearchableSelect } from './SearchableSelect';
import { ShareDraftModal } from './ShareDraftModal';
import { SaveResumeModal } from './SaveResumeModal';
import { saveSubmittedApplication } from './data/applicationsStore';
import { loadFormProgressAsync, SavedFormState } from './data/resumeStore';
import { loadAdminConfig, loadAdminConfigAsync, AdminStoreConfig, getQuestionsForCountry } from './data/adminStore';
import { COUNTRIES } from './questions';

const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID || '';
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || '';
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || '';
const YOUR_EMAIL = process.env.REACT_APP_YOUR_EMAIL || 'myskydestinations@gmail.com';

const VIETNAM_STEP_TITLES = [
  { step: 1, title: 'Checklist & Fees', icon: '📑' },
  { step: 2, title: 'Personal Information', icon: '👤' },
  { step: 3, title: 'Passport & Travel Details', icon: '✈️' },
  { step: 4, title: 'Document Upload (Optional)', icon: '📁' },
];

const VIETNAM_ENTRY_PORTS = [
  'Hanoi - Noi Bai Int Airport (HAN)',
  'Ho Chi Minh City - Tan Son Nhat Int Airport (SGN)',
  'Da Nang Int Airport (DAD)',
  'Phu Quoc Int Airport (PQC)',
  'Nha Trang - Cam Ranh Int Airport (CXR)',
  'Cat Bi Int Airport - Hai Phong (HPH)',
  'Can Tho Int Airport (VCA)',
  'Lao Cai Landport (China Border)',
  'Moc Bai Landport (Cambodia Border)',
];

interface OtherPassportRow {
  passportNumber: string;
  fullName: string;
  dob: string;
  nationality: string;
}

interface HostingOrgRow {
  name: string;
  telephone: string;
  address: string;
  purpose: string;
}

interface PastTripRow {
  fromDate: string;
  toDate: string;
  purpose: string;
}

export const VietnamApp: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [isSending, setIsSending] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showResumeModal, setShowResumeModal] = useState(false);

  const [adminConfig, setAdminConfig] = useState<AdminStoreConfig>(loadAdminConfig);

  useEffect(() => {
    loadAdminConfigAsync().then((conf) => setAdminConfig(conf));
    const handleSync = () => setAdminConfig(loadAdminConfig());
    window.addEventListener('adminConfigUpdated', handleSync);
    return () => window.removeEventListener('adminConfigUpdated', handleSync);
  }, []);

  const dynamicQuestions = getQuestionsForCountry(adminConfig, 'VIETNAM');
  const getQuestion = (id: string) => dynamicQuestions.find((q) => q.id === id);

  const entryPortOptions = getQuestion('entryPort')?.options || VIETNAM_ENTRY_PORTS;
  const exitPortOptions = getQuestion('exitPort')?.options || VIETNAM_ENTRY_PORTS;
  const passportTypeOptions = getQuestion('passportType')?.options || ['Ordinary Passport', 'Diplomatic Passport', 'Official Passport'];
  const genderOptions = getQuestion('gender')?.options || ['Female', 'Male'];

  const [formData, setFormData] = useState({
    surname: '',
    givenName: '',
    dob: '',
    gender: 'Female',
    nationality: 'SRI LANKA',
    identityCard: '',
    email: '',
    reEnterEmail: '',
    religion: '',
    placeOfBirth: '',

    // Question: Used other passports
    usedOtherPassports: 'No',
    otherPassports: [
      { passportNumber: '', fullName: '', dob: '', nationality: 'SRI LANKA' },
    ] as OtherPassportRow[],

    // Question: Multiple nationalities
    hasMultipleNationalities: 'No',
    additionalNationality: '',

    // Question: Law violations
    lawViolation: 'No',

    // Passport & Travel
    passportNumber: '',
    passportType: 'Ordinary Passport',
    passportIssueDate: '',
    passportExpiry: '',
    occupation: '',
    intendedEntryDate: '',
    intendedExitDate: '',
    entryPort: 'Hanoi - Noi Bai Int Airport (HAN)',
    exitPort: 'Ho Chi Minh City - Tan Son Nhat Int Airport (SGN)',
    vietnamAddress: '',

    // Question: Hosting organization / contact
    hasHostingOrg: 'No',
    hostingOrgs: [
      { name: '', telephone: '', address: '', purpose: '' },
    ] as HostingOrgRow[],

    // Question: Past trips in last 1 year
    hasBeenToVietnamLastYear: 'No',
    pastTrips: [
      { fromDate: '', toDate: '', purpose: '' },
    ] as PastTripRow[],
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const resumeCode = params.get('resume');
    if (resumeCode) {
      loadFormProgressAsync(resumeCode).then((saved) => {
        if (saved && saved.formValues) {
          setFormData((prev) => ({ ...prev, ...saved.formValues }));
          if (saved.step) setStep(saved.step);
        }
      });
    }
  }, []);

  const handleChange = (field: string, val: any) => {
    setFormData((prev) => ({ ...prev, [field]: val }));
  };

  const handleAutoFillMRZ = (data: any) => {
    setFormData((prev) => ({
      ...prev,
      surname: data.surname || prev.surname,
      givenName: data.givenName || prev.givenName,
      passportNumber: data.passportNumber || prev.passportNumber,
      dob: data.dobApplicant || prev.dob,
      passportExpiry: data.passportDateExpiry || prev.passportExpiry,
      gender: data.gender === 'FEMALE' ? 'Female' : data.gender === 'MALE' ? 'Male' : prev.gender,
    }));
  };

  const handleRestoreFormState = (saved: SavedFormState) => {
    if (saved.formValues) {
      setFormData((prev) => ({ ...prev, ...saved.formValues }));
    }
    if (saved.step) {
      setStep(saved.step);
    }
  };

  // Handlers for Other Passports Table Rows
  const handleAddOtherPassportRow = () => {
    setFormData((prev) => ({
      ...prev,
      otherPassports: [
        ...prev.otherPassports,
        { passportNumber: '', fullName: '', dob: '', nationality: 'SRI LANKA' },
      ],
    }));
  };

  const handleRemoveOtherPassportRow = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      otherPassports: prev.otherPassports.filter((_, i) => i !== index),
    }));
  };

  const handleOtherPassportRowChange = (index: number, field: keyof OtherPassportRow, value: string) => {
    setFormData((prev) => {
      const updated = [...prev.otherPassports];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, otherPassports: updated };
    });
  };

  // Handlers for Hosting Organizations Table Rows
  const handleAddHostingOrgRow = () => {
    setFormData((prev) => ({
      ...prev,
      hostingOrgs: [
        ...prev.hostingOrgs,
        { name: '', telephone: '', address: '', purpose: '' },
      ],
    }));
  };

  const handleRemoveHostingOrgRow = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      hostingOrgs: prev.hostingOrgs.filter((_, i) => i !== index),
    }));
  };

  const handleHostingOrgRowChange = (index: number, field: keyof HostingOrgRow, value: string) => {
    setFormData((prev) => {
      const updated = [...prev.hostingOrgs];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, hostingOrgs: updated };
    });
  };

  // Handlers for Past Trips Table Rows
  const handleAddPastTripRow = () => {
    setFormData((prev) => ({
      ...prev,
      pastTrips: [
        ...prev.pastTrips,
        { fromDate: '', toDate: '', purpose: '' },
      ],
    }));
  };

  const handleRemovePastTripRow = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      pastTrips: prev.pastTrips.filter((_, i) => i !== index),
    }));
  };

  const handlePastTripRowChange = (index: number, field: keyof PastTripRow, value: string) => {
    setFormData((prev) => {
      const updated = [...prev.pastTrips];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, pastTrips: updated };
    });
  };

  const validateStep = (currentStep: number): boolean => {
    if (currentStep === 2) {
      if (!formData.givenName.trim()) {
        alert('⚠️ Please enter Middle and given name (First name)');
        return false;
      }
      if (!formData.dob) {
        alert('⚠️ Please select Date of birth');
        return false;
      }
      if (!formData.religion.trim()) {
        alert('⚠️ Please enter Religion (e.g. Buddhism)');
        return false;
      }
      if (!formData.placeOfBirth.trim()) {
        alert('⚠️ Please enter Place of birth');
        return false;
      }
    }
    if (currentStep === 3) {
      if (!formData.passportNumber.trim()) {
        alert('⚠️ Please enter Passport Number');
        return false;
      }
      if (!formData.passportExpiry) {
        alert('⚠️ Please select Passport Expiry Date');
        return false;
      }
      if (!formData.intendedEntryDate) {
        alert('⚠️ Please select Intended Date of Entry');
        return false;
      }
      if (!formData.intendedExitDate) {
        alert('⚠️ Please select Intended Date of Exit');
        return false;
      }
      if (!formData.vietnamAddress.trim()) {
        alert('⚠️ Please enter Temporary Hotel / Residential Address in Vietnam');
        return false;
      }
    }
    return true;
  };

  const handleNextStep = () => {
    if (validateStep(step)) {
      setStep((s) => Math.min(s + 1, 4));
    }
  };

  const handleSubmit = async () => {
    setIsSending(true);
    const refId = `MYS-VNM-${Math.floor(10000 + Math.random() * 90000)}`;

    let emailSentSuccess = false;
    if (EMAILJS_SERVICE_ID && EMAILJS_TEMPLATE_ID && EMAILJS_PUBLIC_KEY) {
      try {
        const message = Object.entries(formData)
          .map(([key, val]) => `${key}: ${typeof val === 'object' ? JSON.stringify(val) : val}`)
          .join('\n\n');

        await emailjs.send(
          EMAILJS_SERVICE_ID,
          EMAILJS_TEMPLATE_ID,
          {
            to_email: YOUR_EMAIL,
            from_name: `${formData.givenName} ${formData.surname}`.trim() || 'Vietnam Applicant',
            reply_to: formData.email || YOUR_EMAIL,
            message,
          },
          EMAILJS_PUBLIC_KEY
        );
        emailSentSuccess = true;
      } catch (err) {
        console.warn('EmailJS sending warning:', err);
      }
    }

    saveSubmittedApplication({
      id: refId,
      destination: 'VIETNAM',
      countryName: 'Vietnam',
      applicantName: `${formData.givenName || ''} ${formData.surname || ''}`.trim() || 'Applicant',
      passportNumber: formData.passportNumber || 'N/A',
      nationality: formData.nationality || 'Sri Lanka',
      visaCategory: 'Vietnam e-Visa',
      status: 'Pending 🟡',
      submittedAt: new Date().toLocaleString(),
      totalFeeLkr: 18500,
      data: formData,
    });

    if (emailSentSuccess) {
      alert(`✅ Vietnam e-Visa Application Submitted Successfully!\n\nReference: ${refId}\nEmail notification sent to ${YOUR_EMAIL}.`);
    } else {
      alert(`✅ Vietnam e-Visa Application Submitted Successfully!\n\nReference: ${refId}.`);
    }

    setIsSending(false);
    navigate('/review?country=VIETNAM');
  };

  const progressPercent = Math.round(((step - 1) / (VIETNAM_STEP_TITLES.length - 1)) * 100);

  return (
    <div style={{ maxWidth: '940px', margin: '0 auto', padding: '1.5rem 1rem 5rem' }}>
      {/* HEADER BRANDING */}
      <header
        style={{
          background: 'linear-gradient(135deg, #7f1d1d 0%, #991b1b 50%, #dc2626 100%)',
          borderRadius: '16px',
          padding: '2rem 1.75rem',
          color: '#ffffff',
          boxShadow: '0 10px 25px -5px rgba(153, 27, 27, 0.35)',
          marginBottom: '1.5rem',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
              <span
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(8px)',
                  padding: '0.35rem 0.85rem',
                  borderRadius: '9999px',
                  fontSize: '0.75rem',
                  fontWeight: '700',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                }}
              >
                🇻🇳 Vietnam e-Visa Helper
              </span>
            </div>
            <h1 style={{ fontSize: '1.85rem', fontWeight: '800', marginTop: '0.5rem', margin: 0 }}>
              Vietnam e-Visa Application Form
            </h1>
            <p style={{ color: '#fca5a5', fontSize: '0.9rem', marginTop: '0.25rem', opacity: 0.9 }}>
              Exclusively designed for <strong>mySky Travels</strong> by Madushka Athapaththu
            </p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => navigate('/')}
              style={{
                background: 'linear-gradient(135deg, #fbbf24 0%, #d97706 100%)',
                color: '#0f172a',
                border: '1px solid #fef08a',
                padding: '0.55rem 1rem',
                borderRadius: '10px',
                fontSize: '0.85rem',
                fontWeight: '800',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.4rem',
                boxShadow: '0 4px 14px rgba(217, 119, 6, 0.45)',
                transition: 'all 0.15s ease',
              }}
              title="Return to mySky Destinations Hub"
            >
              🏠 Destinations Hub
            </button>

            <button
              type="button"
              onClick={() => setShowShareModal(true)}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.4)',
                color: '#ffffff',
                padding: '0.55rem 0.9rem',
                borderRadius: '10px',
                fontSize: '0.825rem',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.35rem',
                transition: 'all 0.15s ease',
              }}
            >
              📲 Share Draft
            </button>

            <button
              type="button"
              onClick={() => setShowSaveModal(true)}
              style={{
                background: 'rgba(16, 185, 129, 0.3)',
                border: '1px solid rgba(16, 185, 129, 0.5)',
                color: '#6ee7b7',
                padding: '0.55rem 0.9rem',
                borderRadius: '10px',
                fontSize: '0.825rem',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.35rem',
                transition: 'all 0.15s ease',
              }}
            >
              💾 Save
            </button>

            <button
              type="button"
              onClick={() => setShowResumeModal(true)}
              style={{
                background: 'rgba(99, 102, 241, 0.3)',
                border: '1px solid rgba(99, 102, 241, 0.5)',
                color: '#c7d2fe',
                padding: '0.55rem 0.9rem',
                borderRadius: '10px',
                fontSize: '0.825rem',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.35rem',
                transition: 'all 0.15s ease',
              }}
            >
              🔑 Resume
            </button>

            <div
              style={{
                background: 'rgba(255, 255, 255, 0.15)',
                border: '1px solid rgba(255, 255, 255, 0.25)',
                borderRadius: '10px',
                padding: '0.45rem 0.85rem',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                userSelect: 'none',
              }}
              title="Form Completion Progress"
            >
              <span style={{ fontSize: '0.725rem', color: '#fca5a5', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Progress</span>
              <span style={{ fontSize: '0.95rem', fontWeight: '900', color: '#ffffff', background: 'rgba(255, 255, 255, 0.25)', padding: '0.15rem 0.55rem', borderRadius: '6px', border: '1px solid rgba(255, 255, 255, 0.4)' }}>
                {progressPercent}%
              </span>
            </div>
          </div>
        </div>

        {/* Progress Bar Line */}
        <div
          style={{
            width: '100%',
            height: '8px',
            background: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '9999px',
            marginTop: '1.5rem',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${progressPercent}%`,
              height: '100%',
              background: 'linear-gradient(90deg, #fca5a5 0%, #34d399 100%)',
              transition: 'width 0.4s ease-in-out',
              borderRadius: '9999px',
            }}
          />
        </div>
      </header>

      {/* STEPPER NAVIGATION HEADER */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '0.5rem',
          marginBottom: '2rem',
        }}
      >
        {VIETNAM_STEP_TITLES.map((st) => {
          const isActive = st.step === step;
          const isCompleted = st.step < step;

          return (
            <button
              key={st.step}
              type="button"
              onClick={() => {
                if (isCompleted) setStep(st.step);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 0.85rem',
                borderRadius: '10px',
                border: isActive
                  ? '2px solid #dc2626'
                  : isCompleted
                  ? '1px solid #10b981'
                  : '1px solid #e2e8f0',
                background: isActive ? '#fef2f2' : isCompleted ? '#f0fdf4' : '#ffffff',
                color: isActive ? '#991b1b' : isCompleted ? '#047857' : '#64748b',
                cursor: isCompleted ? 'pointer' : 'default',
                fontWeight: isActive ? '700' : '500',
                fontSize: '0.85rem',
                textAlign: 'left',
                boxShadow: isActive ? '0 4px 12px rgba(220, 38, 38, 0.15)' : 'none',
              }}
            >
              <span
                style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  background: isActive ? '#dc2626' : isCompleted ? '#10b981' : '#e2e8f0',
                  color: isActive || isCompleted ? '#ffffff' : '#64748b',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.75rem',
                  fontWeight: '700',
                  flexShrink: 0,
                }}
              >
                {isCompleted ? '✓' : st.step}
              </span>
              <span style={{ flex: 1 }}>{st.icon} {st.title}</span>
            </button>
          );
        })}
      </div>

      {/* STEP 1: REQUIREMENTS & CHECKLIST */}
      {step === 1 && (
        <RequirementsOverview
          countryCode="VIETNAM"
          onStartApplication={() => setStep(2)}
        />
      )}

      {/* WIZARD STEPS */}
      {step > 1 && (
        <div className="card-container animate-fade-in">
          {step === 2 && (
            <div>
              <h2 style={{ fontSize: '1.3rem', color: '#0f172a', fontWeight: '800', marginBottom: '0.5rem' }}>
                1. PERSONAL INFORMATION
              </h2>

              <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', padding: '0.75rem 1rem', borderRadius: '8px', marginBottom: '1.25rem', fontSize: '0.85rem', color: '#0369a1' }}>
                💡 <em>Enter name as on the ICAO line in the passport. If the passport contains only the Surname or Given names, just enter Given names.</em>
              </div>

              <PassportScanner onAutoFill={handleAutoFillMRZ} />

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem', marginTop: '1rem' }}>
                <div>
                  <label style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1e293b' }}>{getQuestion('surname')?.label || 'Surname (Last name):'}</label>
                  <input
                    type="text"
                    value={formData.surname}
                    onChange={(e) => handleChange('surname', e.target.value.toUpperCase())}
                    placeholder={getQuestion('surname')?.placeholder || 'e.g. MUNAWEERA THANTHRIGE'}
                    style={{ marginTop: '0.35rem' }}
                  />
                </div>

                <div>
                  <label style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1e293b' }}>{getQuestion('givenName')?.label || '*Middle and given name (First name):'}</label>
                  <input
                    type="text"
                    value={formData.givenName}
                    onChange={(e) => handleChange('givenName', e.target.value.toUpperCase())}
                    placeholder={getQuestion('givenName')?.placeholder || 'e.g. YASHMI NAVODA'}
                    style={{ marginTop: '0.35rem' }}
                  />
                </div>

                <div>
                  <label style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1e293b' }}>{getQuestion('dob')?.label || '*Date of birth:'}</label>
                  <input
                    type="date"
                    value={formData.dob}
                    onChange={(e) => handleChange('dob', e.target.value)}
                    style={{ marginTop: '0.35rem' }}
                  />
                </div>

                <div>
                  <label style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1e293b' }}>{getQuestion('gender')?.label || '*Sex:'}</label>
                  <select
                    value={formData.gender}
                    onChange={(e) => handleChange('gender', e.target.value)}
                    style={{ marginTop: '0.35rem' }}
                  >
                    {genderOptions.map((g) => (
                      <option key={g} value={g}>{g}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1e293b' }}>{getQuestion('nationality')?.label || '*Nationality:'}</label>
                  <SearchableSelect
                    id="vietnam_nationality"
                    options={COUNTRIES}
                    value={formData.nationality}
                    onChange={(val) => handleChange('nationality', val)}
                    placeholder="Select Nationality..."
                  />
                </div>

                <div>
                  <label style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1e293b' }}>{getQuestion('identityCard')?.label || 'Identity Card / ID Card (Optional):'}</label>
                  <input
                    type="text"
                    value={formData.identityCard}
                    onChange={(e) => handleChange('identityCard', e.target.value.toUpperCase())}
                    placeholder={getQuestion('identityCard')?.placeholder || 'ID Card Number'}
                    style={{ marginTop: '0.35rem' }}
                  />
                </div>

                <div>
                  <label style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1e293b' }}>{getQuestion('email')?.label || '*Email Address:'}</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder={getQuestion('email')?.placeholder || 'e.g. myskydestinations@gmail.com'}
                    style={{ marginTop: '0.35rem' }}
                  />
                </div>

                <div>
                  <label style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1e293b' }}>{getQuestion('reEnterEmail')?.label || '*Re-enter Email Address:'}</label>
                  <input
                    type="email"
                    value={formData.reEnterEmail}
                    onChange={(e) => handleChange('reEnterEmail', e.target.value)}
                    placeholder={getQuestion('reEnterEmail')?.placeholder || 'Re-enter Email'}
                    style={{ marginTop: '0.35rem' }}
                  />
                </div>

                <div>
                  <label style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1e293b' }}>{getQuestion('religion')?.label || '*Religion:'}</label>
                  <input
                    type="text"
                    value={formData.religion}
                    onChange={(e) => handleChange('religion', e.target.value.toUpperCase())}
                    placeholder={getQuestion('religion')?.placeholder || 'e.g. Buddhism'}
                    style={{ marginTop: '0.35rem' }}
                  />
                </div>

                <div>
                  <label style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1e293b' }}>{getQuestion('placeOfBirth')?.label || '*Place of birth:'}</label>
                  <input
                    type="text"
                    value={formData.placeOfBirth}
                    onChange={(e) => handleChange('placeOfBirth', e.target.value.toUpperCase())}
                    placeholder={getQuestion('placeOfBirth')?.placeholder || 'e.g. COLOMBO'}
                    style={{ marginTop: '0.35rem' }}
                  />
                </div>
              </div>

              {/* ADDITIONAL QUESTIONS FROM OFFICIAL PORTAL WITH DYNAMIC TABLES */}
              <div style={{ marginTop: '1.5rem', paddingTop: '1.25rem', borderTop: '1px solid #cbd5e1' }}>
                {/* Question: Used other passports */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1e293b' }}>
                    Have you ever used any other passports to enter into Viet Nam?
                  </label>
                  <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.35rem', marginBottom: '0.75rem' }}>
                    <label><input type="radio" name="usedOtherPassports" value="No" checked={formData.usedOtherPassports === 'No'} onChange={(e) => handleChange('usedOtherPassports', e.target.value)} /> No</label>
                    <label><input type="radio" name="usedOtherPassports" value="Yes" checked={formData.usedOtherPassports === 'Yes'} onChange={(e) => handleChange('usedOtherPassports', e.target.value)} /> Yes</label>
                  </div>

                  {formData.usedOtherPassports === 'Yes' && (
                    <div style={{ overflowX: 'auto', background: '#f8fafc', padding: '1rem', borderRadius: '10px', border: '1px solid #cbd5e1' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                        <thead>
                          <tr style={{ background: '#f1f5f9', borderBottom: '2px solid #cbd5e1', textAlign: 'left' }}>
                            <th style={{ padding: '0.5rem' }}>No</th>
                            <th style={{ padding: '0.5rem' }}>Passport</th>
                            <th style={{ padding: '0.5rem' }}>Full name</th>
                            <th style={{ padding: '0.5rem' }}>Date of birth</th>
                            <th style={{ padding: '0.5rem' }}>Nationality</th>
                            <th style={{ padding: '0.5rem', textAlign: 'center' }}>Add/delete</th>
                          </tr>
                        </thead>
                        <tbody>
                          {formData.otherPassports.map((row, idx) => (
                            <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                              <td style={{ padding: '0.5rem', fontWeight: '700' }}>{idx + 1}</td>
                              <td style={{ padding: '0.5rem' }}>
                                <input
                                  type="text"
                                  placeholder="Enter passport"
                                  value={row.passportNumber}
                                  onChange={(e) => handleOtherPassportRowChange(idx, 'passportNumber', e.target.value.toUpperCase())}
                                  style={{ padding: '0.35rem', fontSize: '0.8rem' }}
                                />
                              </td>
                              <td style={{ padding: '0.5rem' }}>
                                <input
                                  type="text"
                                  placeholder="Enter full name"
                                  value={row.fullName}
                                  onChange={(e) => handleOtherPassportRowChange(idx, 'fullName', e.target.value.toUpperCase())}
                                  style={{ padding: '0.35rem', fontSize: '0.8rem' }}
                                />
                              </td>
                              <td style={{ padding: '0.5rem' }}>
                                <input
                                  type="date"
                                  value={row.dob}
                                  onChange={(e) => handleOtherPassportRowChange(idx, 'dob', e.target.value)}
                                  style={{ padding: '0.35rem', fontSize: '0.8rem' }}
                                />
                              </td>
                              <td style={{ padding: '0.5rem' }}>
                                <SearchableSelect
                                  id={`other_nat_${idx}`}
                                  options={COUNTRIES}
                                  value={row.nationality}
                                  onChange={(val) => handleOtherPassportRowChange(idx, 'nationality', val)}
                                  placeholder="Choose nationality"
                                />
                              </td>
                              <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                                {formData.otherPassports.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveOtherPassportRow(idx)}
                                    style={{ border: 'none', background: 'transparent', color: '#dc2626', cursor: 'pointer', fontWeight: '800', fontSize: '1rem' }}
                                  >
                                    🗑️
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>

                      <div style={{ marginTop: '0.75rem', textAlign: 'right' }}>
                        <button
                          type="button"
                          onClick={handleAddOtherPassportRow}
                          style={{ background: '#f0fdf4', border: '1px solid #86efac', color: '#166534', padding: '0.35rem 0.85rem', borderRadius: '6px', fontSize: '0.8rem', fontWeight: '700', cursor: 'pointer' }}
                        >
                          ➕ Add Row
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Question: Multiple nationalities */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1e293b' }}>
                    Do you have multiple nationalities?
                  </label>
                  <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.35rem' }}>
                    <label><input type="radio" name="hasMultipleNationalities" value="No" checked={formData.hasMultipleNationalities === 'No'} onChange={(e) => handleChange('hasMultipleNationalities', e.target.value)} /> No</label>
                    <label><input type="radio" name="hasMultipleNationalities" value="Yes" checked={formData.hasMultipleNationalities === 'Yes'} onChange={(e) => handleChange('hasMultipleNationalities', e.target.value)} /> Yes</label>
                  </div>

                  {formData.hasMultipleNationalities === 'Yes' && (
                    <div style={{ marginTop: '0.75rem', maxWidth: '350px' }}>
                      <SearchableSelect id="add_multi_nat" options={COUNTRIES} value={formData.additionalNationality} onChange={(val) => handleChange('additionalNationality', val)} placeholder="Select Additional Nationality..." />
                    </div>
                  )}
                </div>

                {/* Question: Law violations */}
                <div>
                  <label style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1e293b' }}>
                    Violation of the Vietnamese laws/regulations (if any)
                  </label>
                  <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.35rem' }}>
                    <label><input type="radio" name="lawViolation" value="No" checked={formData.lawViolation === 'No'} onChange={(e) => handleChange('lawViolation', e.target.value)} /> No</label>
                    <label><input type="radio" name="lawViolation" value="Yes" checked={formData.lawViolation === 'Yes'} onChange={(e) => handleChange('lawViolation', e.target.value)} /> Yes</label>
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <h2 style={{ fontSize: '1.3rem', color: '#0f172a', fontWeight: '800', marginBottom: '1.25rem', borderBottom: '2px solid #dc2626', paddingBottom: '0.5rem' }}>
                2. PASSPORT & TRAVEL INFORMATION
              </h2>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem' }}>
                <div>
                  <label style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1e293b' }}>{getQuestion('passportNumber')?.label || '*Passport Number:'}</label>
                  <input
                    type="text"
                    value={formData.passportNumber}
                    onChange={(e) => handleChange('passportNumber', e.target.value.toUpperCase())}
                    placeholder={getQuestion('passportNumber')?.placeholder || 'e.g. N12345678'}
                    style={{ marginTop: '0.35rem' }}
                  />
                </div>

                <div>
                  <label style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1e293b' }}>{getQuestion('passportType')?.label || '*Passport Type:'}</label>
                  <select
                    value={formData.passportType}
                    onChange={(e) => handleChange('passportType', e.target.value)}
                    style={{ marginTop: '0.35rem' }}
                  >
                    {passportTypeOptions.map((pt) => (
                      <option key={pt} value={pt}>{pt}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1e293b' }}>{getQuestion('passportIssueDate')?.label || 'Passport Date of Issue:'}</label>
                  <input
                    type="date"
                    value={formData.passportIssueDate}
                    onChange={(e) => handleChange('passportIssueDate', e.target.value)}
                    style={{ marginTop: '0.35rem' }}
                  />
                </div>

                <div>
                  <label style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1e293b' }}>{getQuestion('passportExpiry')?.label || '*Passport Expiry Date:'}</label>
                  <input
                    type="date"
                    value={formData.passportExpiry}
                    onChange={(e) => handleChange('passportExpiry', e.target.value)}
                    style={{ marginTop: '0.35rem' }}
                  />
                </div>

                <div>
                  <label style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1e293b' }}>{getQuestion('occupation')?.label || 'Occupation (Optional):'}</label>
                  <input
                    type="text"
                    value={formData.occupation}
                    onChange={(e) => handleChange('occupation', e.target.value.toUpperCase())}
                    placeholder={getQuestion('occupation')?.placeholder || 'e.g. BUSINESS PERSON / ENGINEER'}
                    style={{ marginTop: '0.35rem' }}
                  />
                </div>

                <div>
                  <label style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1e293b' }}>{getQuestion('intendedEntryDate')?.label || '*Intended Date of Entry:'}</label>
                  <input
                    type="date"
                    value={formData.intendedEntryDate}
                    onChange={(e) => handleChange('intendedEntryDate', e.target.value)}
                    style={{ marginTop: '0.35rem' }}
                  />
                </div>

                <div>
                  <label style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1e293b' }}>{getQuestion('intendedExitDate')?.label || '*Intended Date of Exit:'}</label>
                  <input
                    type="date"
                    value={formData.intendedExitDate}
                    onChange={(e) => handleChange('intendedExitDate', e.target.value)}
                    style={{ marginTop: '0.35rem' }}
                  />
                </div>

                <div>
                  <label style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1e293b' }}>{getQuestion('entryPort')?.label || '*Allowed Entry Checkpoint (Port):'}</label>
                  <select
                    value={formData.entryPort}
                    onChange={(e) => handleChange('entryPort', e.target.value)}
                    style={{ marginTop: '0.35rem' }}
                  >
                    {entryPortOptions.map((port) => (
                      <option key={port} value={port}>
                        {port}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1e293b' }}>{getQuestion('exitPort')?.label || '*Allowed Exit Checkpoint (Port):'}</label>
                  <select
                    value={formData.exitPort}
                    onChange={(e) => handleChange('exitPort', e.target.value)}
                    style={{ marginTop: '0.35rem' }}
                  >
                    {exitPortOptions.map((port) => (
                      <option key={port} value={port}>
                        {port}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ marginTop: '1.25rem' }}>
                <label style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1e293b' }}>{getQuestion('vietnamAddress')?.label || '*Temporary Hotel / Residential Address in Vietnam:'}</label>
                <textarea
                  rows={3}
                  value={formData.vietnamAddress}
                  onChange={(e) => handleChange('vietnamAddress', e.target.value.toUpperCase())}
                  placeholder={getQuestion('vietnamAddress')?.placeholder || 'e.g. Lotte Hotel Hanoi, 54 Lieu Giai, Ba Dinh, Hanoi'}
                  style={{ marginTop: '0.35rem' }}
                />
              </div>

              {/* DYNAMIC QUESTION TABLES FOR TRAVEL INFORMATION */}
              <div style={{ marginTop: '1.75rem', paddingTop: '1.25rem', borderTop: '1px solid #cbd5e1' }}>
                {/* Question: Hosting Organization / Individual Contact */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1e293b' }}>
                    Agency/Organization/Individual that the applicant plans to contact when enter into Viet Nam?
                  </label>
                  <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.35rem', marginBottom: '0.75rem' }}>
                    <label><input type="radio" name="hasHostingOrg" value="No" checked={formData.hasHostingOrg === 'No'} onChange={(e) => handleChange('hasHostingOrg', e.target.value)} /> No</label>
                    <label><input type="radio" name="hasHostingOrg" value="Yes" checked={formData.hasHostingOrg === 'Yes'} onChange={(e) => handleChange('hasHostingOrg', e.target.value)} /> Yes</label>
                  </div>

                  {formData.hasHostingOrg === 'Yes' && (
                    <div style={{ overflowX: 'auto', background: '#f8fafc', padding: '1rem', borderRadius: '10px', border: '1px solid #cbd5e1' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                        <thead>
                          <tr style={{ background: '#f1f5f9', borderBottom: '2px solid #cbd5e1', textAlign: 'left' }}>
                            <th style={{ padding: '0.5rem' }}>No</th>
                            <th style={{ padding: '0.5rem' }}>Name of hosting organization</th>
                            <th style={{ padding: '0.5rem' }}>Telephone number</th>
                            <th style={{ padding: '0.5rem' }}>Address</th>
                            <th style={{ padding: '0.5rem' }}>Purpose</th>
                            <th style={{ padding: '0.5rem', textAlign: 'center' }}>Add/delete</th>
                          </tr>
                        </thead>
                        <tbody>
                          {formData.hostingOrgs.map((row, idx) => (
                            <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                              <td style={{ padding: '0.5rem', fontWeight: '700' }}>{idx + 1}</td>
                              <td style={{ padding: '0.5rem' }}>
                                <input
                                  type="text"
                                  placeholder="Enter name of hosting organization"
                                  value={row.name}
                                  onChange={(e) => handleHostingOrgRowChange(idx, 'name', e.target.value.toUpperCase())}
                                  style={{ padding: '0.35rem', fontSize: '0.8rem' }}
                                />
                              </td>
                              <td style={{ padding: '0.5rem' }}>
                                <input
                                  type="text"
                                  placeholder="Enter telephone number"
                                  value={row.telephone}
                                  onChange={(e) => handleHostingOrgRowChange(idx, 'telephone', e.target.value)}
                                  style={{ padding: '0.35rem', fontSize: '0.8rem' }}
                                />
                              </td>
                              <td style={{ padding: '0.5rem' }}>
                                <input
                                  type="text"
                                  placeholder="Enter address"
                                  value={row.address}
                                  onChange={(e) => handleHostingOrgRowChange(idx, 'address', e.target.value.toUpperCase())}
                                  style={{ padding: '0.35rem', fontSize: '0.8rem' }}
                                />
                              </td>
                              <td style={{ padding: '0.5rem' }}>
                                <input
                                  type="text"
                                  placeholder="Enter purpose"
                                  value={row.purpose}
                                  onChange={(e) => handleHostingOrgRowChange(idx, 'purpose', e.target.value.toUpperCase())}
                                  style={{ padding: '0.35rem', fontSize: '0.8rem' }}
                                />
                              </td>
                              <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                                {formData.hostingOrgs.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveHostingOrgRow(idx)}
                                    style={{ border: 'none', background: 'transparent', color: '#dc2626', cursor: 'pointer', fontWeight: '800', fontSize: '1rem' }}
                                  >
                                    🗑️
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>

                      <div style={{ marginTop: '0.75rem', textAlign: 'right' }}>
                        <button
                          type="button"
                          onClick={handleAddHostingOrgRow}
                          style={{ background: '#f0fdf4', border: '1px solid #86efac', color: '#166534', padding: '0.35rem 0.85rem', borderRadius: '6px', fontSize: '0.8rem', fontWeight: '700', cursor: 'pointer' }}
                        >
                          ➕ Add Row
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Question: Past trips in last 01 year */}
                <div>
                  <label style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1e293b' }}>
                    Have you been to Viet Nam in the last 01 year?
                  </label>
                  <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.35rem', marginBottom: '0.75rem' }}>
                    <label><input type="radio" name="hasBeenToVietnamLastYear" value="No" checked={formData.hasBeenToVietnamLastYear === 'No'} onChange={(e) => handleChange('hasBeenToVietnamLastYear', e.target.value)} /> No</label>
                    <label><input type="radio" name="hasBeenToVietnamLastYear" value="Yes" checked={formData.hasBeenToVietnamLastYear === 'Yes'} onChange={(e) => handleChange('hasBeenToVietnamLastYear', e.target.value)} /> Yes</label>
                  </div>

                  {formData.hasBeenToVietnamLastYear === 'Yes' && (
                    <div style={{ overflowX: 'auto', background: '#f8fafc', padding: '1rem', borderRadius: '10px', border: '1px solid #cbd5e1' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                        <thead>
                          <tr style={{ background: '#f1f5f9', borderBottom: '2px solid #cbd5e1', textAlign: 'left' }}>
                            <th style={{ padding: '0.5rem' }}>No</th>
                            <th style={{ padding: '0.5rem' }}>From date</th>
                            <th style={{ padding: '0.5rem' }}>To date</th>
                            <th style={{ padding: '0.5rem' }}>Purpose of trip</th>
                            <th style={{ padding: '0.5rem', textAlign: 'center' }}>Add/delete</th>
                          </tr>
                        </thead>
                        <tbody>
                          {formData.pastTrips.map((row, idx) => (
                            <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                              <td style={{ padding: '0.5rem', fontWeight: '700' }}>{idx + 1}</td>
                              <td style={{ padding: '0.5rem' }}>
                                <input
                                  type="date"
                                  value={row.fromDate}
                                  onChange={(e) => handlePastTripRowChange(idx, 'fromDate', e.target.value)}
                                  style={{ padding: '0.35rem', fontSize: '0.8rem' }}
                                />
                              </td>
                              <td style={{ padding: '0.5rem' }}>
                                <input
                                  type="date"
                                  value={row.toDate}
                                  onChange={(e) => handlePastTripRowChange(idx, 'toDate', e.target.value)}
                                  style={{ padding: '0.35rem', fontSize: '0.8rem' }}
                                />
                              </td>
                              <td style={{ padding: '0.5rem' }}>
                                <input
                                  type="text"
                                  placeholder="Enter purpose"
                                  value={row.purpose}
                                  onChange={(e) => handlePastTripRowChange(idx, 'purpose', e.target.value.toUpperCase())}
                                  style={{ padding: '0.35rem', fontSize: '0.8rem' }}
                                />
                              </td>
                              <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                                {formData.pastTrips.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => handleRemovePastTripRow(idx)}
                                    style={{ border: 'none', background: 'transparent', color: '#dc2626', cursor: 'pointer', fontWeight: '800', fontSize: '1rem' }}
                                  >
                                    🗑️
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>

                      <div style={{ marginTop: '0.75rem', textAlign: 'right' }}>
                        <button
                          type="button"
                          onClick={handleAddPastTripRow}
                          style={{ background: '#f0fdf4', border: '1px solid #86efac', color: '#166534', padding: '0.35rem 0.85rem', borderRadius: '6px', fontSize: '0.8rem', fontWeight: '700', cursor: 'pointer' }}
                        >
                          ➕ Add Row
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', borderBottom: '2px solid #dc2626', paddingBottom: '0.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                <h2 style={{ fontSize: '1.3rem', color: '#0f172a', fontWeight: '800', margin: 0 }}>
                  FOREIGNER'S IMAGES (4x6 cm & Passport Scan)
                </h2>
                <span style={{ fontSize: '0.8rem', color: '#047857', background: '#ecfdf5', border: '1px solid #a7f3d0', padding: '0.35rem 0.75rem', borderRadius: '9999px', fontWeight: '700' }}>
                  ℹ️ Optional Step
                </span>
              </div>
              <DocumentUpload countryCode="VIETNAM" />
            </div>
          )}

          {/* NAVIGATION CONTROLS */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '2rem', paddingTop: '1rem', borderTop: '1px solid #e2e8f0', flexWrap: 'wrap', gap: '0.75rem' }}>
            <button type="button" onClick={() => setStep((s) => Math.max(s - 1, 1))} className="btn-secondary">
              ← Back
            </button>

            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <button
                type="button"
                onClick={() => setShowSaveModal(true)}
                className="btn-secondary"
                style={{ color: '#059669', borderColor: '#10b981' }}
              >
                💾 Save Draft
              </button>

              {step < 4 ? (
                <button
                  type="button"
                  onClick={handleNextStep}
                  className="btn-primary"
                  style={{ background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)' }}
                >
                  Save & Next →
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSending}
                  className="btn-primary"
                  style={{ background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)' }}
                >
                  {isSending ? 'Sending Email...' : 'Submit Vietnam e-Visa ✅'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showShareModal && (
        <ShareDraftModal
          applicantName={`${formData.givenName || ''} ${formData.surname || ''}`.trim() || 'Applicant'}
          formData={formData}
          onClose={() => setShowShareModal(false)}
        />
      )}

      {showSaveModal && (
        <SaveResumeModal
          mode="SAVE"
          destination="Vietnam"
          step={step}
          formValues={formData}
          onClose={() => setShowSaveModal(false)}
          onRestore={handleRestoreFormState}
        />
      )}

      {showResumeModal && (
        <SaveResumeModal
          mode="RESUME"
          destination="Vietnam"
          step={step}
          onClose={() => setShowResumeModal(false)}
          onRestore={handleRestoreFormState}
        />
      )}
    </div>
  );
};
